import type { GamificationTrophyItem, TrophyId } from '@crowdfpl/domain/gamification';
import { Card } from '@crowdfpl/ui-web/card';
import { Sparkles } from 'lucide-react';
import { type ReactElement } from 'react';
import { trackUmamiEvent } from '@/helpers/analytics';
import { AlmostThereSection } from './almost-there-section';
import { RarityChip, TrophyCard, TrophyCardProgress } from './trophy-card';
import { TrophyConditions } from './trophy-conditions';
import {
  formatTrophyTitle,
  TROPHY_ACCENT,
  TROPHY_CONDITIONS,
  TROPHY_EMOJI,
  TROPHY_TAGLINE_COPY,
} from './trophy-copy';
import { trophyTierToneClass } from './trophy-tier-styles';
import { buildTrophySystemSections, resolveFeaturedTrophy } from './trophy-view-model';

type TrophyCardSlotProps = {
  isOwnProfile?: boolean;
  pinnedTrophyId?: TrophyId | null;
  onPin?: (trophyId: TrophyId) => void;
  isPinPending?: boolean;
};

function SectionHeader({
  title,
  count,
  total,
  hint,
}: {
  title: string;
  count?: number;
  total?: number;
  hint?: string;
}): ReactElement {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <div className="flex min-w-0 items-baseline gap-2">
        <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-foreground">
          {title}
        </h4>
        {count !== undefined ? (
          <span className="text-[10px] font-semibold tabular-nums text-muted-foreground">
            {count}
            {total !== undefined ? (
              <span className="text-muted-foreground/60">/{total}</span>
            ) : null}
          </span>
        ) : null}
      </div>
      {hint ? (
        <span className="truncate text-[10px] text-muted-foreground/80">{hint}</span>
      ) : null}
    </div>
  );
}

function FeaturedTrophyCard({ trophy }: { trophy: GamificationTrophyItem }): ReactElement {
  const accent = TROPHY_ACCENT[trophy.trophy_id];
  const title = formatTrophyTitle(trophy.trophy_id);
  const tierLabel =
    trophy.highest_tier != null
      ? trophy.highest_tier.charAt(0).toUpperCase() + trophy.highest_tier.slice(1)
      : null;

  return (
    <Card
      className="relative flex flex-col gap-3 overflow-hidden p-4 ring-2 ring-gamification-rare-ring shadow-[0_0_28px_hsl(var(--gamification-rare)/0.22)]"
      data-testid="featured-trophy-card"
      data-gamification-motion="decorative"
      onClick={() => {
        trackUmamiEvent('gamification_trophy_click', {
          trophy_id: trophy.trophy_id,
          status: trophy.status,
        });
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full opacity-35 blur-3xl"
        style={{ background: accent }}
      />
      <div className="relative flex items-start gap-3" data-testid="featured-trophy-header">
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-3xl"
          style={{
            background: `color-mix(in srgb, ${accent} 12%, transparent)`,
            boxShadow: `0 0 0 1px color-mix(in srgb, ${accent} 33%, transparent) inset`,
          }}
          aria-hidden
        >
          {TROPHY_EMOJI[trophy.trophy_id]}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-gamification-rare/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gamification-rare-foreground">
              <Sparkles className="h-2.5 w-2.5" aria-hidden /> Featured
            </span>
            {tierLabel ? (
              <span
                className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${trophyTierToneClass(trophy.highest_tier)}`}
              >
                {tierLabel}
              </span>
            ) : null}
            {trophy.kind === 'one_shot' ? (
              <span className="rounded-full bg-foreground/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-foreground">
                Unlocked
              </span>
            ) : null}
            <RarityChip rarityPct={trophy.rarity_pct} />
          </div>
          <h3 className="mt-1 text-base font-black leading-tight text-foreground">{title}</h3>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            {TROPHY_TAGLINE_COPY[trophy.trophy_id]}
          </p>
        </div>
      </div>

      <TrophyCardProgress trophy={trophy} />

      <TrophyConditions conditions={TROPHY_CONDITIONS[trophy.trophy_id]} trophyId={trophy.trophy_id} />
    </Card>
  );
}

type TrophySystemViewProps = {
  trophies: readonly GamificationTrophyItem[];
  isOwnProfile?: boolean;
  pinnedTrophyId?: TrophyId | null;
  onPin?: (trophyId: TrophyId) => void;
  isPinPending?: boolean;
};

/**
 * Trophy collection: header stat bar, Almost There, Featured (pinned), Unlocked, Locked.
 * Own-profile mode can pin unlocked trophies via the card click.
 */
export function TrophySystemView({
  trophies,
  isOwnProfile = false,
  pinnedTrophyId = null,
  onPin,
  isPinPending = false,
}: TrophySystemViewProps): ReactElement {
  const sections = buildTrophySystemSections(trophies);
  const featured = resolveFeaturedTrophy(trophies, pinnedTrophyId);
  const cardProps: TrophyCardSlotProps = { isOwnProfile, pinnedTrophyId, onPin, isPinPending };
  const unlockedCount = sections.yourTrophies.length;
  const unlockedWithoutFeatured = featured
    ? sections.yourTrophies.filter(trophy => trophy.trophy_id !== featured.trophy_id)
    : sections.yourTrophies;
  const almostThere = featured
    ? sections.almostThere.filter(trophy => trophy.trophy_id !== featured.trophy_id)
    : sections.almostThere;

  return (
    <div data-testid="trophies-view-slot" className="space-y-5" aria-label="Trophy system">
      <Card className="bg-gradient-to-br from-gamification-rare/10 via-card to-card p-3.5 sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-[15px] font-black leading-tight tracking-tight text-foreground sm:text-base">
              Seven trophies. Seven ways to be great.
            </h3>
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
              Pin one, it shows on your profile and next to your name.
            </p>
          </div>
          <div className="shrink-0 text-center">
            <div className="text-lg font-black leading-none tabular-nums text-foreground sm:text-xl">
              {unlockedCount}
              <span className="text-xs text-muted-foreground sm:text-sm">/{trophies.length}</span>
            </div>
            <div className="mt-1 text-[9px] font-bold uppercase tracking-wider text-muted-foreground sm:text-[10px]">
              Unlocked
            </div>
          </div>
        </div>
      </Card>

      <AlmostThereSection trophies={almostThere} {...cardProps} />

      {featured ? (
        <section aria-label="Featured trophy" data-testid="featured-trophy-section">
          <SectionHeader title="Featured" />
          <FeaturedTrophyCard trophy={featured} />
        </section>
      ) : null}

      {unlockedWithoutFeatured.length > 0 ? (
        <section aria-label="Your Trophies" data-testid="your-trophies-section">
          <SectionHeader title="Your Trophies" count={unlockedWithoutFeatured.length} />
          <div className="grid gap-2.5 sm:grid-cols-2">
            {unlockedWithoutFeatured.map(trophy => (
              <TrophyCard key={trophy.trophy_id} trophy={trophy} {...cardProps} />
            ))}
          </div>
        </section>
      ) : null}

      {sections.locked.length > 0 ? (
        <section aria-label="Locked trophies" data-testid="locked-trophies-section">
          <SectionHeader title="Locked" count={sections.locked.length} />
          <div className="grid gap-2.5 sm:grid-cols-2">
            {sections.locked.map(trophy => (
              <TrophyCard key={trophy.trophy_id} trophy={trophy} {...cardProps} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
